import { type RefObject, useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { Button, Divider, Icon } from "semantic-ui-react";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import type { ItemRefs } from "components/ideas/useIdeaScrollTracking";

import { ImpactStatementEditor } from "../../ImpactStatements/ImpactStatementEditor";
import { StageSegment } from "../../ImpactStatements/StageSegments";

type ImpactStatements =
  OpenAPI.GET<"/ideas/{idea_id}/impactStatement">["response"]["impactStatements"];

type ImpactStatementManagementProps = {
  idea: OpenAPI.GET<"/ideas/{id}">["response"];
  sectionRefs?: RefObject<ItemRefs>;
  scrollRef?: RefObject<HTMLDivElement>;
};

const ImpactStatementManagement = ({
  idea,
}: ImpactStatementManagementProps) => {
  const { t } = useTranslation();
  const [currentImpactStage, setCurrentImpactStage] = useState<
    NonNullable<ImpactStatements["userEstimate"]>["stage"] | null
  >(null);
  const [isCreatingStage, setIsCreatingStage] = useState("");
  const [detailsViewStage, setDetailsViewStage] = useState<string | null>(null);
  const user = useAppSelector((state) => state.user);
  const { projectManagement = {} } = idea;
  const { boards = [] } = projectManagement;
  const pmBoardObject =
    boards.length ?
      boards.find((b) => b.forId === idea?.ownerChallenge?._id)
    : null;
  const userIsAssigned = useMemo(() => {
    const isAssigned = (pmBoardObject?.assignees || []).find(
      (assigneeId) => assigneeId === user._id,
    );
    return !!isAssigned;
  }, [user, pmBoardObject]);

  const impactStatementQuery = useQuery(
    initUseQuery(api.ideas.getImpactStatements, [idea._id]),
  );

  const createImpactStatement = useCallback(
    (
      newStatement: {
        impacts?: OpenAPI.Schemas["ImpactStatement"]["impacts"];
        description?: OpenAPI.Schemas["ImpactStatement"]["description"];
      },
      forStage: OpenAPI.Schemas["ImpactStatement"]["stage"],
    ) => {
      setIsCreatingStage(forStage);
      api.ideas.createImpactStatement(
        idea._id,
        {
          impacts: newStatement?.impacts ?? {},
          description: newStatement?.description,
          stage: forStage,
        },
        ({ impactStatement: newImpactStatement }) => {
          setIsCreatingStage("");
          api.setQueryData(
            api.ideas.getImpactStatements,
            [idea._id],
            (prev) => ({
              ...prev,
              impactStatements: {
                ...prev.impactStatements,
                [forStage]: newImpactStatement,
              },
            }),
          );

          setCurrentImpactStage(forStage);
          util.onUpdateIdea(idea, {
            completedImpactStages: {
              ...(idea.completedImpactStages || {}),
              [forStage]: true,
            },
          });
        },
        () => {
          setIsCreatingStage("");
          toast.error(
            `Failed to save ${t("generic.impact")} statement. Please try again.`,
          );
        },
      );
    },
    [idea, t],
  );

  const updateImpactStatements = useCallback(
    (impactStatements: ImpactStatements) => {
      api.invalidateQueries(api.challenges.getIdeaImpacts, [idea.challenge]);
      api.setQueryData(api.ideas.getImpactStatements, [idea._id], (prev) => ({
        ...prev,
        impactStatements,
      }));
    },
    [idea],
  );

  const deleteStatement = (impactStatementId: string) => {
    api.ideas.deleteImpactStatement(
      idea._id,
      impactStatementId,
      () => {
        toast.success("Statement deleted successfully");
        api.invalidateQueries(api.challenges.getIdeas, [idea.challenge]);
        api.setQueryData(api.ideas.getImpactStatements, [idea._id], (prev) => ({
          ...prev,
          impactStatements: {
            ...prev.impactStatements,
            [impactStatementId]: undefined,
          },
        }));
      },
      () => {
        toast.error("Failed to delete statement");
      },
    );
  };

  if (currentImpactStage) {
    return (
      <div>
        <Button basic compact onClick={() => setCurrentImpactStage(null)}>
          <Icon name="arrow left" />
          Back to overview
        </Button>

        <Divider />
        <ImpactStatementEditor
          impactStatements={impactStatementQuery.data?.impactStatements || {}}
          forStage={currentImpactStage}
          setImpactStatements={updateImpactStatements}
          statementDefintion={idea.ownerChallenge.impactStatements}
          ideaId={idea._id}
          restrictStagesTo={[currentImpactStage]}
          editable={
            currentImpactStage !== "userEstimate" &&
            (userIsAssigned ||
              util.hasPermission(
                user,
                "challenge.editProjectImpacts",
                idea.challenge,
              ) ||
              util.canManageChallenge(user, idea.ownerChallenge))
          }
        />

        <Divider hidden />
        <Button
          secondary
          onClick={() => {
            setCurrentImpactStage(null);
          }}
        >
          <Icon name="check" />
          Done
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div>
        {detailsViewStage === "userEstimate" && (
          <Button basic compact onClick={() => setDetailsViewStage(null)}>
            <Icon name="arrow left" />
            Back to overview
          </Button>
        )}
      </div>
      {idea.ownerChallenge?.impactStatements?.showOnIdea ?
        <StageSegment
          stage="userEstimate"
          statementDefintion={idea.ownerChallenge.impactStatements}
          impactStatement={
            impactStatementQuery.data?.impactStatements?.userEstimate
          }
          onView={() => {
            setCurrentImpactStage("userEstimate");
          }}
        />
      : null}
      <div>
        {detailsViewStage === "projectEstimate" && (
          <Button basic compact onClick={() => setDetailsViewStage(null)}>
            <Icon name="arrow left" />
            Back to overview
          </Button>
        )}
      </div>
      <StageSegment
        stage="projectEstimate"
        statementDefintion={idea.ownerChallenge.impactStatements}
        canEditStage={
          userIsAssigned ||
          util.hasPermission(
            user,
            "challenge.editProjectImpacts",
            idea.challenge,
          ) ||
          util.canManageChallenge(user, idea.ownerChallenge)
        }
        impactStatement={
          impactStatementQuery.data?.impactStatements?.projectEstimate
        }
        onView={() => {
          setCurrentImpactStage("projectEstimate");
        }}
        isCreating={isCreatingStage === "projectEstimate"}
        onCreate={() => {
          createImpactStatement(
            {
              impacts:
                impactStatementQuery.data?.impactStatements?.userEstimate
                  ?.impacts ?? {},
              description:
                impactStatementQuery.data?.impactStatements?.userEstimate
                  ?.description,
            },
            "projectEstimate",
          );
        }}
        onDelete={() => {
          util
            .confirm(
              "Delete the project estimate?",
              "Are you sure you want to delete the project estimate statement? This action is instant and irreversible.",
            )
            .then(() => {
              if (
                !impactStatementQuery.data?.impactStatements?.projectEstimate
                  ?._id
              ) {
                return;
              }
              deleteStatement(
                impactStatementQuery.data.impactStatements.projectEstimate._id,
              );
              util.onUpdateIdea(idea, {
                completedImpactStages: {
                  ...(idea.completedImpactStages || {}),
                  projectEstimate: false,
                },
              });
            })

            .catch(() => {});
        }}
      />
      <div>
        {detailsViewStage === "finalOutcome" && (
          <Button basic compact onClick={() => setDetailsViewStage(null)}>
            <Icon name="arrow left" />
            Back to overview
          </Button>
        )}
      </div>
      <StageSegment
        stage="finalOutcome"
        statementDefintion={idea.ownerChallenge.impactStatements}
        impactStatement={
          impactStatementQuery.data?.impactStatements?.finalOutcome
        }
        canEditStage={
          userIsAssigned ||
          util.hasPermission(
            user,
            "challenge.editProjectImpacts",
            idea.challenge,
          ) ||
          util.canManageChallenge(user, idea.ownerChallenge)
        }
        onView={() => {
          setCurrentImpactStage("finalOutcome");
        }}
        onCreate={() => {
          createImpactStatement(
            {
              impacts:
                impactStatementQuery.data?.impactStatements?.projectEstimate
                  ?.impacts ?? {},
              description:
                impactStatementQuery.data?.impactStatements?.projectEstimate
                  ?.description,
            },
            "finalOutcome",
          );
        }}
        onDelete={() => {
          util
            .confirm(
              "Delete final outcome?",
              "Are you sure you want to delete the final outcome statement? This action is instant and irreversible.",
            )
            .then(() => {
              if (
                !impactStatementQuery.data?.impactStatements?.finalOutcome?._id
              ) {
                return;
              }
              deleteStatement(
                impactStatementQuery.data.impactStatements.finalOutcome._id,
              );
              util.onUpdateIdea(idea, {
                completedImpactStages: {
                  ...(idea.completedImpactStages || {}),
                  finalOutcome: false,
                },
              });
            })
            .catch(() => {});
        }}
      />
    </div>
  );
};

export default ImpactStatementManagement;
